'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useTheme } from '@/hooks/useTheme';

export default function ForgotPasswordPage() {
    const router = useRouter();
    const { theme, toggleTheme } = useTheme();
    const [email, setEmail] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);
        setLoading(true);

        try {
            const supabase = createClient();

            // Get the current origin for the redirect URL
            const redirectUrl = `${window.location.origin}/reset-password`;

            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: redirectUrl,
            });

            if (error) {
                setError(error.message);
                return;
            }

            setSuccess(true);
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            console.error('Password reset error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center px-6 py-12">
            {/* Theme Toggle Button */}
            <button
                onClick={toggleTheme}
                className="fixed top-6 right-6 p-3 bg-card border border-border rounded-lg shadow-lg hover:bg-accent/10 transition-colors duration-200 z-50"
                aria-label="Toggle theme"
            >
                {theme === 'light' ? (
                    <Icon name="MoonIcon" size={20} variant="outline" className="text-foreground" />
                ) : (
                    <Icon name="SunIcon" size={20} variant="outline" className="text-foreground" />
                )}
            </button>

            <div className="w-full max-w-md">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                        <Icon name="KeyIcon" size={32} variant="outline" className="text-primary" />
                    </div>
                    <h1 className="text-3xl font-headline font-bold text-foreground mb-2">
                        Forgot Password?
                    </h1>
                    <p className="text-muted-foreground">
                        No worries! Enter your email and we'll send you a reset link.
                    </p>
                </div>

                {/* Reset Password Card */}
                <div className="bg-card border border-border rounded-2xl shadow-lg p-8">
                    {/* Success Message */}
                    {success && (
                        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-start gap-3">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-green-600 mt-0.5" />
                                <div>
                                    <p className="text-sm font-semibold text-green-800 mb-1">
                                        Check your email!
                                    </p>
                                    <p className="text-sm text-green-700">
                                        We've sent a password reset link to <strong>{email}</strong>.
                                        Click the link in the email to reset your password.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-start gap-3">
                                <Icon name="XCircleIcon" size={20} variant="solid" className="text-red-600 mt-0.5" />
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        </div>
                    )}

                    {!success ? (
                        <>
                            {/* Reset Form */}
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Email */}
                                <div>
                                    <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                                        Email Address
                                    </label>
                                    <input
                                        id="email"
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                        placeholder="you@company.com"
                                    />
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground"></div>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Icon name="PaperAirplaneIcon" size={16} variant="outline" />
                                            Send Reset Link
                                        </>
                                    )}
                                </button>
                            </form>

                            {/* Back to Sign In */}
                            <div className="mt-6 text-center">
                                <a href="/signin" className="text-sm text-primary font-semibold hover:underline inline-flex items-center gap-2">
                                    <Icon name="ArrowLeftIcon" size={16} variant="outline" />
                                    Back to Sign In
                                </a>
                            </div>
                        </>
                    ) : (
                        <div className="space-y-4">
                            {/* Resend Button */}
                            <button
                                onClick={handleSubmit}
                                disabled={loading}
                                className="w-full px-6 py-3 bg-muted text-foreground rounded-lg font-semibold hover:bg-muted/80 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Sending...' : 'Resend Email'}
                            </button>

                            {/* Back to Sign In */}
                            <div className="text-center">
                                <a href="/signin" className="text-sm text-primary font-semibold hover:underline inline-flex items-center gap-2">
                                    <Icon name="ArrowLeftIcon" size={16} variant="outline" />
                                    Back to Sign In
                                </a>
                            </div>
                        </div>
                    )}
                </div>

                {/* Help Text */}
                <div className="mt-6 text-center">
                    <p className="text-sm text-muted-foreground">
                        Didn't receive the email? Check your spam folder or{' '}
                        <a href="/contact" className="text-primary hover:underline">contact support</a>
                    </p>
                </div>
            </div>
        </main>
    );
}
